import React, { lazy, Suspense } from 'react';

import SkeletonLines from '~/components/core/Skeletons/SkeletonLines';
import { useCms } from '~/components/hooks/useCms';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isBoostOrganization, isFeatureEnabled } from '~/Utils';

import InnerTabs from '../../core/Layout/InnerTabs/InnerTabs';
import useOrganization from '../../OrganizationContext';
import EmailConfiguration from '../EmailConfiguration/EmailConfiguration';
import GeneralEmailConfig from '../GeneralQueue/GeneralEmailConfig';
import OperationsBreadcrumbs from '../OperationsBreadcrumbs';
import OrganizationCommunicationsTab from '../OrganizationCommunicationsTab';
import { useSysconfig } from '../SystemConfigurationScreen';

import CommunicationGeneralConfig from './CommunicationGeneralConfig/CommunicationGeneralConfig';
import DOCUMENT_REQUEST_TEXTS from './DocumentRequestConfigurationTab/texts';
import DyteVideoConfigurationTab from './DyteVideoConfigurationTab/DyteVideoConfigurationTab';
import PhoneConfigurationTab from './PhoneConfigurationTab/PhoneConfigurationTab';
import PhysicalMailConfigurationTab from './PhysicalMailConfigurationTab/PhysicalMailConfigurationTab';
import QueueManagementTab from './QueueManagementTab/QueueManagementTab';

// Loaded only when the tab renders, which InnerTabs does only while the feature flag enables it.
const LazyDocumentRequestConfigurationTab = lazy(
  () => import('./DocumentRequestConfigurationTab/DocumentRequestConfigurationTab')
);

const DocumentRequestConfigurationTabLoader = () => (
  <Suspense fallback={<SkeletonLines count={6} className="w-full p-32" />}>
    <LazyDocumentRequestConfigurationTab />
  </Suspense>
);

const CommunicationConfigurations = () => {
  const { userOrganization } = useCms(); // this is the non impersonated organization when in the non impersonated admin panel
  const { isTwilioEnabled, isDyteVideoInitialized, isDyteVideoEnabled } = useOrganization();
  const { organizationOperationalDetails, organization: sysconfigOrganization } = useSysconfig();

  const showTwilioConfigTabs = isTwilioEnabled;
  const tabs = [
    {
      label: 'General',
      url: 'communication_general',
      component: CommunicationGeneralConfig,
      disableTab: false,
    },
    {
      label: 'Email Configuration',
      url: 'email_configuration',
      component: EmailConfiguration,
      disableTab: isBoostOrganization(sysconfigOrganization),
    },
    {
      label: 'General Email Config',
      url: 'general_email_classification',
      component: GeneralEmailConfig,
      disableTab: false,
    },
    {
      label: 'Phone Configuration',
      url: 'phone_configuration',
      component: PhoneConfigurationTab,
      disableTab: !showTwilioConfigTabs,
    },
    {
      label: 'Queue Management',
      url: 'queue_management',
      component: QueueManagementTab,
      disableTab: !showTwilioConfigTabs || !organizationOperationalDetails.twilioConfiguration.is_queues_enabled,
    },
    {
      label: 'Users Configuration',
      url: 'users_communications',
      component: OrganizationCommunicationsTab,
      disableTab: !userOrganization.is_super_organization,
    },
    {
      label: 'Video Calls Config',
      url: 'dyte_video_config',
      component: DyteVideoConfigurationTab,
      disableTab: userOrganization.is_super_organization ? !isDyteVideoInitialized : !isDyteVideoEnabled,
    },
    {
      label: DOCUMENT_REQUEST_TEXTS.tabLabel,
      url: 'document_request',
      component: DocumentRequestConfigurationTabLoader,
      disableTab: !isFeatureEnabled(sysconfigOrganization, CONFIGURATION_FEATURES_NAMES.DOCUMENT_REQUESTS),
    },
    {
      label: 'Physical Mail',
      url: 'physical_mail',
      component: PhysicalMailConfigurationTab,
      disableTab: !userOrganization.is_super_organization,
    },
  ];

  return (
    <>
      <OperationsBreadcrumbs currentTab="Communications" addPadding />
      <InnerTabs tabs={tabs} />
    </>
  );
};

export default CommunicationConfigurations;
